<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>

        <label class="spl-inline-form mb-1" :for="componentId">
            {{label}}
            <base-popover-icon
                v-if="popoverContent"
                :title="label"
                :content="popoverContent"
                class="ml-2"
            ></base-popover-icon>
        </label>
        <multiselect 
            v-model="selectedTags"
            :id="componentId" 
            label="name" 
            track-by="uuid" 
            tag-placeholder="Add this as new tag"
            tag-position="bottom"
            :options="tagOptions" 
            :multiple="true" 
            :taggable="true"
            :loading="loading" 
            :internal-search="true" 
            :clear-on-select="true" 
            :close-on-select="false" 
            :limit-text="limitText" 
            :max-height="500" 
            :disabled="disabled"
            :show-no-results="false" 
            :hide-selected="true"
            @tag="addTag"
        >
            <span slot="noResult">
                Oops! No tags found. Consider changing the search query.
            </span>
        </multiselect>
        <!-- selectedTagsIds: {{selectedTagsIds}} - {{selectedTagsIds.length}}
        <br>
        value prop: {{value}} - {{value.length}} -->
    </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import { suppliosApi } from '../js/api-service';
//import isEqual from 'lodash/isEqual';
import { isSameArray } from '../js/filters-helpers';
import kebabCase from 'lodash/kebabCase';
//import uniqueId from 'lodash/uniqueId';
import { v4 as uuidv4 } from 'uuid';


export default {
    name: 'SourcingTagsMultiselect',
    components: {
        Multiselect,
    },
    props: {
        label: {
            type: String,
            default: 'Sourcing Tags',
        },
        value: {  // v-model connects to this
            // should be an array of tag IDs
            type: Array,
            required: false,
        },
        popoverContent: {
            type: String,
            default: 'Add tags to track things by project, region, item type, etc.  They are a freeform way to track and group sourcing events, and are fully searchable.',
        },
        readOnly: {  // if true, it is read-only, not for editing
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            selectedTags: [],  
            selectedUUIDs: [],  // keep a list of all UUIDs that are selected, then filter on this to send objects to Multiselect
            tagOptions: [],  // get from API, then when you create new ones, wait for API response, then push into here --> so all these have all data
        }
    },
    computed: {
        componentId: function() {
            return 'multiselect-' + kebabCase(this.label);
        },
        selectedTagsIds: function() {
            // get the IDs for tags we've selected... filter our any that don't have IDs (yet), then return IDs
            // this still does not react when you add an ID to something you already pushed here... so to handle 'new' tags, you need to do that separately in your new tag handler chain
            // add a watcher to this, and emit this list to parent when changed
            return this.selectedTags.filter(tag => tag.id).map(tag => tag.id);
        },
    },
    watch: {
        value: {
            immediate: true,  // need this so it fires right away
            handler: function() {
                this.setSelected();
            },
        },
        selectedTagsIds: function(newIds, oldIds) {
            // watching the list of 'currently selected tag IDs'
            // this won't catch ALL changes... for new tags, it will likely miss them because computed property won't catch updates within objects (like we do with 'updating' the PK when we get from backend)
            // so this will handle and emit updates for selecting existing tags, and our other method will handle and emit for new tags
            // they may both fire at times, but that's OK, the data shouldn't conflict ???
            //if (isEqual(newIds, oldIds)) return;  // I don't think this works for comparing arrays with different order...
            if (isSameArray(newIds, oldIds)) return; // our own array similarity checker
            this.emitToParent(newIds);
        },
    },
    mounted: function() {
        this.getTags();
    },
    methods: {
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        limitText: function(count) {
            return `and ${count} other tags`;
        },
        setSelected: function() {
            // updates the selected tags based on array of IDs from parent
            if (this.value && this.value.length) {
                this.selectedTags = this.tagOptions.filter(opt => this.value.includes(opt.id));
            }
        },
        getTags: function() {
            this.loading = true;
            suppliosApi.sourcingTag.list()
            .then(response => {
                console.log('got tags, response below');
                console.log(response);
                this.tagOptions = response.data;
                this.setSelected();  // may not do anything if we don't have value from parent yet
            })
            .catch(error => this.handleError(error, 'Error while getting tags'))
            .finally(() => this.loading = false)
        },
        addTag: function(newTag) {  // @tag only fires for NEW tags not in the options list
            const newTagObj = {
                name: newTag,
                uuid: uuidv4(),
            };
            console.log('making a new tag below');
            console.log(newTagObj);
            this.tagOptions.push(newTagObj);
            this.selectedTags.push(newTagObj);
            this.addTagToBackend(newTagObj);
        },
        addTagToBackend: function(newTagObj) {
            suppliosApi.sourcingTag.create(newTagObj)
            .then(response => {
                console.log('added new tag to backend, response below');
                console.log(response);
                this.updateNewWithSaved(response.data);  // "enrich" our previously pushed tags with PK, etc.
            })
            .catch(error => this.handleError(error, 'Error while saving new tag'))
        },
        updateNewWithSaved: function(fromApi) {
            // updates or 'enriches' the new tag object we pushed to tagOptions array with new data from API
            // doesn't actually replace the object, but does Object.assign() so it gives it ID and created values
            let foundTagOption = this.tagOptions[this.tagOptions.findIndex(tag => tag.uuid === fromApi.uuid)];
            console.log('found tagOption:');
            console.log(foundTagOption);
            Object.assign(foundTagOption, fromApi);
            let foundSelectedTagsData = this.selectedTags[this.selectedTags.findIndex(tag => tag.uuid === fromApi.uuid)];
            console.log('found selectedTags:');
            console.log(foundSelectedTagsData);
            Object.assign(foundSelectedTagsData, fromApi);
            // now finally update parent with new IDs
            // better to do it here rather than computed property because computed properties won't react to upated nested data (from Object.assign above)
            // here, we know we updated the arrays, so just pull IDs out from v-model data prop and send up
            let currentIds = this.selectedTags.map(sel => sel.id);
            this.emitToParent(currentIds);
        },
        emitToParent: function(idArray) {
            // just separating out because we call from multiple places
            this.$emit('input', idArray);
        },
        clearTags: function() {
            this.selectedTags = []
        },
    }
}
</script>

<style>

</style>